import axios, { AxiosResponse } from "axios";
import { WebAPI, IFailCallbackParam, AnyType } from "../constants";
import { getErrorStatus } from "./getErrorStatus";
import AuthService from "../../common/AuthService";
import { toLocaleString } from "./common";

type InternalNoticeRow = AnyType;

export interface IGetNoticesResponceModel {
  /**
   * 通知ID
   */
  noticeid: string;
  /**
   * 通知発生時刻
   */
  noticetime: string;
  /**
   * 未読の場合：true、既読の場合：false
   */
  unread: boolean;
  /**
   * 通知レベル
   */
  level: string;
  /**
   * 通知メッセージ
   */
  message: string;
  /**
   * 通知対象のプロジェクト名
   */
  projectname: string;
  /**
   * 情報へのuri
   */
  uri: string;
}

export interface INoticesRow extends IGetNoticesResponceModel {
  id: number;
}

interface ICallbackParams extends IFailCallbackParam {
  /**
   * WebAPIから通知の取得が成功した場合に呼び出されます
   * @callback success
   * @param {INoticesRow[]} rows - 通知一覧
   * @param {number} status - httpステータス
   */
  success: (rows: INoticesRow[], status: number) => void;
}

function createRow(index: number, row: InternalNoticeRow): INoticesRow {
  row.id = index;
  row.noticetime = toLocaleString(new Date(Date.parse(row.noticetime)));
  return row;
}

function createRows(data: IGetNoticesResponceModel[]): INoticesRow[] {
  if (Array.isArray(data)) {
    const rows: InternalNoticeRow[] = data.slice();
    rows.forEach((row, i) => {
      row = createRow(i, row);
    });
    return rows;
  } else {
    return [createRow(0, data)];
  }
}

function AxiosGet(url: string, token?: string) {
  if (token) {
    return axios.get(url, {
      headers: {
        Authorization: "Bearer " + token,
      },
    });
  } else {
    return axios.get(url);
  }
}

interface IAxiosInternalParams extends ICallbackParams {
  promise: Promise<AxiosResponse>;
}

function AxiosInternal(params: IAxiosInternalParams) {
  params.promise
    .then((res) => {
      params.success(createRows(res.data), res.status);
    })
    .catch((err) => {
      const { id, status } = getErrorStatus(err);
      params.fail(id, status);
    });
}

export interface IGetNoticesParams extends ICallbackParams {
  /**
   * 認証オブジェクト
   */
  authService?: AuthService;
  /**
   * 通知メッセージの言語
   */
  lang: string;
  /**
   * 未読のみ：ture
   */
  unread?: string;
}

/**
 * 通知一覧を取得します
 * @function getNotices
 * @param {IGetNoticesParams} params - 通知一覧取得パラメータ
 */
export function getNotices(params: IGetNoticesParams): void {
  const { authService, lang, unread } = params;
  let url = WebAPI.NOTICES + "?lang=" + lang;
  if (unread) {
    url = url + "&unread=" + unread;
  }
  if (authService) {
    authService.getToken({
      success: (token) => {
        AxiosInternal({
          promise: AxiosGet(url, token),
          success: params.success,
          fail: (id, status) => {
            if (status === 401) {
              getNoticesRetry401(authService, url, params);
            } else {
              params.fail(id, status);
            }
          },
        });
      },
      fail: (err) => {
        const { id, status } = getErrorStatus(err);
        params.fail(id, status);
      },
    });
  } else {
    AxiosInternal({
      promise: AxiosGet(url),
      success: params.success,
      fail: params.fail,
    });
  }
}

/**
 * 通知一覧を取得します
 * @function getNoticesRetry401
 * @param {AuthService} authService - 認証オブジェクト
 * @param {string} url - リクエストURL
 * @param {ICallbackParams} params - コールバックパラメータ
 */
function getNoticesRetry401(
  authService: AuthService,
  url: string,
  params: ICallbackParams
): void {
  authService.updateToken({
    success: (token) => {
      AxiosInternal({
        promise: AxiosGet(url, token),
        success: params.success,
        fail: params.fail,
      });
    },
    fail: (err) => {
      const { id, status } = getErrorStatus(err);
      params.fail(id, status);
    },
  });
}
